import React from 'react';
import type { Tone } from '../../components/ui/tokens';
import { FOCUS } from '../../components/ui/tokens';
import type { TrailBox } from './lifecycleModel';

/** The dot colour of each tone: the one owner, read by the trail, its record list and the legend. */
export const TONE_DOT: Record<Tone, string> = {
  positive: 'bg-emerald-500',
  attention: 'bg-amber-500',
  negative: 'bg-red-500',
  overdue: 'bg-red-600',
  neutral: 'bg-slate-400',
};

export interface LifecycleTrailProps {
  boxes: TrailBox[];
  /** Whether this person may open documents of the box's kind (the page's own permission). */
  canOpen: (box: TrailBox) => boolean;
  /** A box with documents was clicked. `anchor` is the button, so a list of several can sit under it. */
  onSelect: (box: TrailBox, anchor: HTMLElement) => void;
  /** Which box has its list of documents open, if any. */
  openKey?: string | null;
  /** Names the strip for screen readers, e.g. "Lifecycle of RFQ-1042". */
  label: string;
}

/**
 * One RFQ's lifecycle as a strip of boxes: RFQ, Quotation, Order, Vendor PO, Vendor Invoice, Client
 * Invoice, Credit Note, Payment. Draws what `buildTrail` returned and decides nothing itself.
 *
 * A box with documents is a button. One document opens it; several open a list under the box (the
 * list's owner places it). A box with nothing in it is not a button, because there is nothing to open.
 */
const LifecycleTrail: React.FC<LifecycleTrailProps> = ({ boxes, canOpen, onSelect, openKey, label }) => (
  <div role="group" aria-label={label} className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 shadow-sm sm:grid-cols-4 xl:grid-cols-8">
    {boxes.map((box) => {
      const filled = box.state === 'filled';
      const multi = box.docs.length > 1;
      const interactive = filled && canOpen(box);
      const body = (
        <>
          <div className="flex items-center gap-1.5 text-xs text-slate-500 whitespace-nowrap">
            {filled && multi ? (
              <span className="rounded-full bg-primary-600 px-1.5 text-xs font-bold leading-[18px] text-white">{box.docs.length}</span>
            ) : filled ? (
              <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${TONE_DOT[box.tone]}`} />
            ) : null}
            {box.label}
          </div>
          <div
            className={[
              'truncate text-sm',
              filled ? 'font-semibold text-slate-900' : 'font-normal text-slate-400',
              box.state === 'loading' ? 'h-5' : '',
            ].join(' ')}
            title={filled ? box.docs.map((d) => d.title).join(', ') : undefined}
          >
            {box.state === 'loading' ? (
              <span aria-label="Loading" className="mt-1 block h-3 w-3/4 animate-pulse rounded bg-slate-100" />
            ) : (
              box.value
            )}
          </div>
          {box.sub ? <div className="truncate text-xs text-slate-500">{box.sub}</div> : null}
        </>
      );

      // The 1px gaps over the strip's slate background are the dividers, at any number of columns.
      const cell = 'min-w-0 px-3 py-2';
      return interactive ? (
        <button
          key={box.key}
          type="button"
          aria-haspopup={multi ? 'menu' : undefined}
          aria-expanded={multi ? openKey === box.key : undefined}
          onClick={(e) => onSelect(box, e.currentTarget)}
          className={`${cell} text-left ${openKey === box.key ? 'bg-primary-50' : 'bg-white hover:bg-primary-50'} ${FOCUS}`}
        >
          {body}
        </button>
      ) : (
        <div key={box.key} className={`${cell} ${filled ? 'bg-white' : 'bg-slate-50'}`}>
          {body}
        </div>
      );
    })}
  </div>
);

export default LifecycleTrail;
